/**
 * Delete my account — client-side rules and wording (13 Oct 2026).
 * Server side and the full list of what is deleted: functions/accountDeletion.js.
 *
 * Apple 5.1.1(v) ("Offering account deletion in your app", read 13 Oct 2026):
 * deletion must be initiated IN the app, remove the account record and its
 * personal data, may ask for a confirmation step, must not require an email
 * or support flow, and must tell subscribers that billing continues through
 * the store until they cancel. Apps using Sign in with Apple that hold no
 * Apple tokens (ZoneIn holds none) delete the data and direct the user to
 * revoke the app in their Apple ID settings (Apple TN3194).
 */

/** What the user types to confirm — explicit, but not a hoop. */
export const CONFIRM_WORD = 'DELETE';

export function isConfirmed(typed: string): boolean {
  return typed.trim().toUpperCase() === CONFIRM_WORD;
}

/** Apple's page for removing an app from "Sign in with Apple" (TN3194). */
export const APPLE_REVOKE_HELP_URL = 'https://support.apple.com/en-us/102571';

const DEFAULT_URL = 'https://us-central1-zonein-82a22.cloudfunctions.net/deleteAccount';

/**
 * The deleteAccount function sits next to the AI relay (same region and
 * project): derive it from VITE_AI_RELAY_URL so a moved project moves both.
 */
export function deleteAccountUrl(relayUrl: string | undefined): string {
  const relay = (relayUrl ?? '').trim();
  if (/^https:\/\/[^/]+\/ai$/.test(relay)) return relay.replace(/\/ai$/, '/deleteAccount');
  return DEFAULT_URL;
}

/** The server's reply, in words — unknown failures keep their detail (CLAUDE.md §2). */
export function describeDeleteFailure(status: number, body: unknown): string {
  const b = (body && typeof body === 'object' ? body : {}) as { error?: unknown; detail?: unknown };
  const error = typeof b.error === 'string' ? b.error : '';
  const detail = typeof b.detail === 'string' ? ` (${b.detail})` : '';
  if (status === 401) return 'Your sign-in has expired — sign in again, then delete the account.';
  if (status === 0) return 'No connection — check your internet and try again. Nothing was deleted.';
  return `${error || `Account deletion failed (HTTP ${status}).`}${detail}`;
}
